import { jsPDF } from 'jspdf';
import { AssessmentResult, ImplementationPlan } from '../types';

export function generateValidatePDF(result: AssessmentResult, email: string): void {
  const doc = new jsPDF();
  let yPos = 20;
  
  // Header
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('HIPAA Compliance Assessment', 105, yPos, { align: 'center' });
  yPos += 10;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated for: ${email}`, 105, yPos, { align: 'center' });
  yPos += 5;
  doc.text(`Date: ${new Date().toLocaleDateString()}`, 105, yPos, { align: 'center' });
  yPos += 15;
  
  // Score Box
  doc.setFillColor(59, 130, 246);
  doc.roundedRect(60, yPos, 90, 35, 3, 3, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(36);
  doc.setFont('helvetica', 'bold');
  doc.text(`${result.percentage}%`, 105, yPos + 15, { align: 'center' });
  doc.setFontSize(12);
  doc.text(`${result.totalScore} / ${result.maxScore} points`, 105, yPos + 25, { align: 'center' });
  doc.setFontSize(14);
  doc.text(`${result.riskLevel} RISK`, 105, yPos + 32, { align: 'center' });
  yPos += 45;
  
  doc.setTextColor(0, 0, 0);
  
  // Projected Improvement
  if (result.riskLevel !== 'LOW') {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Projected Improvement:', 20, yPos);
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    doc.text(`${result.projectedImprovement.currentScore}% → ${result.projectedImprovement.projectedScore}% in ${result.projectedImprovement.timeframe}`, 20, yPos);
    yPos += 12;
  }
  
  // Category Breakdown
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Score Breakdown by Category', 20, yPos);
  yPos += 8;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  result.categoryScores.forEach((cat) => {
    doc.text(`${cat.category}: ${cat.percentage}% (${cat.score}/${cat.maxScore})`, 25, yPos);
    yPos += 6;
    if (yPos > 270) {
      doc.addPage();
      yPos = 20;
    }
  });
  yPos += 8;
  
  // Strengths
  if (result.strengths && result.strengths.length > 0) {
    if (yPos > 220) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Your Strengths', 20, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    result.strengths.forEach((strength, i) => {
      doc.setFont('helvetica', 'bold');
      doc.text(`${i + 1}. ${strength.title}`, 25, yPos);
      yPos += 5;
      doc.setFont('helvetica', 'normal');
      const descLines = doc.splitTextToSize(strength.description, 160);
      descLines.forEach((line: string) => {
        doc.text(line, 30, yPos);
        yPos += 5;
      });
      yPos += 3;
      
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
    });
    yPos += 5;
  }
  
  // Critical Gaps
  if (result.criticalGaps.length > 0) {
    if (yPos > 200) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Areas for Improvement', 20, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    result.criticalGaps.forEach((gap, i) => {
      doc.setFont('helvetica', 'bold');
      doc.text(`${i + 1}. ${gap.title}`, 25, yPos);
      yPos += 5;
      doc.setFont('helvetica', 'normal');
      doc.text(`Severity: ${gap.severity}`, 30, yPos);
      yPos += 5;
      const actionLines = doc.splitTextToSize(`Action: ${gap.action}`, 150);
      actionLines.forEach((line: string) => {
        doc.text(line, 30, yPos);
        yPos += 5;
      });
      doc.text(`Timeframe: ${gap.timeframe}`, 30, yPos);
      yPos += 8;
      
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
    });
  } else {
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(34, 197, 94);
    doc.text('Excellent! No critical gaps identified.', 20, yPos);
    doc.setTextColor(0, 0, 0);
    yPos += 10;
  }
  
  // 30-Day Roadmap
  if (yPos > 200) {
    doc.addPage();
    yPos = 20;
  }
  
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('30-Day Remediation Roadmap', 20, yPos);
  yPos += 8;
  
  doc.setFontSize(10);
  result.roadmap.forEach((week) => {
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setFont('helvetica', 'bold');
    doc.text(`Week ${week.week}: ${week.title}`, 25, yPos);
    yPos += 6;
    
    doc.setFont('helvetica', 'normal');
    week.tasks.forEach((task) => {
      const taskLines = doc.splitTextToSize(`• ${task.task}`, 160);
      taskLines.forEach((line: string) => {
        doc.text(line, 30, yPos);
        yPos += 5;
      });
    });
    yPos += 4;
    
    if (yPos > 270) {
      doc.addPage();
      yPos = 20;
    }
  });
  
  doc.save(`HIPAA-Assessment-${Date.now()}.pdf`);
}

export function generateGettingStartedPDF(plan: ImplementationPlan, email: string): void {
  const doc = new jsPDF();
  let yPos = 20;
  
  // Header
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('HIPAA Implementation Roadmap', 105, yPos, { align: 'center' });
  yPos += 10;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated for: ${email}`, 105, yPos, { align: 'center' });
  yPos += 5;
  doc.text(`Date: ${new Date().toLocaleDateString()}`, 105, yPos, { align: 'center' });
  yPos += 15;
  
  // Organization Profile
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Your Profile', 20, yPos);
  yPos += 8;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Organization Type: ${plan.organizationProfile.orgType}`, 25, yPos);
  yPos += 6;
  doc.text(`Cloud Provider: ${plan.organizationProfile.cloudProvider}`, 25, yPos);
  yPos += 6;
  doc.text(`Timeline: ${plan.organizationProfile.timeline}`, 25, yPos);
  yPos += 6;
  doc.text(`Current Stage: ${plan.organizationProfile.stage}`, 25, yPos);
  yPos += 6;
  doc.text(`Team Size: ${plan.organizationProfile.size}`, 25, yPos);
  yPos += 6;
  doc.text(`Uses AI/ML: ${plan.organizationProfile.usesAI ? 'Yes' : 'No'}`, 25, yPos);
  yPos += 12;
  
  // Estimates
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Implementation Estimates', 20, yPos);
  yPos += 8;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Estimated Time: ${plan.summary.estimatedTime}`, 25, yPos);
  yPos += 6;
  doc.text(`Estimated Cost: ${plan.summary.estimatedCost}`, 25, yPos);
  yPos += 6;
  doc.text(`Total Items: ${plan.summary.totalItems}`, 25, yPos);
  yPos += 12;
  
  // Monthly Phases
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('4-Month Implementation Plan', 20, yPos);
  yPos += 8;
  
  doc.setFontSize(10);
  plan.monthlyPhases.forEach((phase) => {
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setFont('helvetica', 'bold');
    doc.text(`Month ${phase.week}: ${phase.title}`, 25, yPos);
    yPos += 6;
    
    doc.setFont('helvetica', 'normal');
    phase.tasks.slice(0, 3).forEach((task) => {
      const lines = doc.splitTextToSize(`• ${task.task}`, 160);
      doc.text(lines[0], 30, yPos);
      yPos += 5;
    });
    yPos += 6;
  });
  
  doc.save(`HIPAA-Implementation-Plan-${Date.now()}.pdf`);
}
